/**
 * Form Pre-fill Manager
 * Handles field mapping, transformations, and step hiding logic
 */

import { ExternalLeadData } from '../api/lead-data-fetcher'

// Partial form data type (subset of full FormData from unified-lead-tracker)
export interface PreFilledFormData {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  street?: string
  city?: string
  state?: string
  zip?: string
  dob?: string
  ssn?: string
  loan_amount?: string | number
  housing_status?: string
  housing_payment?: string | number
  employment_status?: string
  income?: string | number
  [key: string]: any
}

/**
 * Map external API lead data to form field names
 * @param leadData - Data from external API
 * @returns Mapped form data with correct field names and transformations
 */
export function mapExternalLeadToFormData(leadData: ExternalLeadData): PreFilledFormData {
  const formData: PreFilledFormData = {}

  // Direct string mappings (no transformation)
  if (leadData.firstName) {
    formData.first_name = leadData.firstName.trim()
  }
  if (leadData.lastName) {
    formData.last_name = leadData.lastName.trim()
  }
  if (leadData.email) {
    formData.email = leadData.email.trim()
  }

  // Phone number - remove dashes, spaces, parentheses
  if (leadData.phoneNumber) {
    formData.phone = cleanPhoneNumber(leadData.phoneNumber)
  }

  // Address fields
  if (leadData.streetAddress) {
    formData.street = leadData.streetAddress.trim()
  }
  if (leadData.city) {
    formData.city = leadData.city.trim()
  }
  if (leadData.state) {
    formData.state = leadData.state.trim().toUpperCase() // Ensure uppercase state code
  }
  if (leadData.zipCode) {
    formData.zip = leadData.zipCode.trim()
  }

  // DOB - keep MM/DD/YYYY format
  if (leadData.dob) {
    formData.dob = leadData.dob.trim()
  }

  // SSN - keep XXX-XX-XXXX format with dashes
  if (leadData.ssn) {
    formData.ssn = leadData.ssn.trim()
  }

  // Loan amount - convert to number if valid
  if (leadData.requestedLoanAmount) {
    const amount = parseFloat(leadData.requestedLoanAmount)
    if (!isNaN(amount)) {
      formData.loan_amount = amount
    } else {
      formData.loan_amount = leadData.requestedLoanAmount.trim()
    }
  }

  // Housing fields (may be empty - user will fill these)
  if (leadData.housingStatus && leadData.housingStatus.trim().length > 0) {
    formData.housing_status = leadData.housingStatus.trim()
  }
  if (leadData.housingPayment && leadData.housingPayment.trim().length > 0) {
    const payment = parseFloat(leadData.housingPayment)
    if (!isNaN(payment)) {
      formData.housing_payment = payment
    } else {
      formData.housing_payment = leadData.housingPayment.trim()
    }
  }

  // Employment fields (may be empty - user will fill these)
  if (leadData.employmentStatus && leadData.employmentStatus.trim().length > 0) {
    formData.employment_status = leadData.employmentStatus.trim()
  }
  if (leadData.grossAnnualIncome && leadData.grossAnnualIncome.trim().length > 0) {
    const income = parseFloat(leadData.grossAnnualIncome)
    if (!isNaN(income)) {
      formData.income = income
    } else {
      formData.income = leadData.grossAnnualIncome.trim()
    }
  }

  // Include refId for tracking
  if (leadData.refId) {
    formData.refId = leadData.refId
  }

  console.log('✅ Mapped API data to form fields:', {
    fieldsPopulated: Object.keys(formData).length,
    fields: Object.keys(formData)
  })

  return formData
}

/**
 * Clean phone number - remove dashes, spaces, parentheses
 * @param phone - Phone number with potential formatting
 * @returns Clean phone number (digits only)
 */
function cleanPhoneNumber(phone: string): string {
  return phone.replace(/[\s\-\(\)]/g, '').trim()
}

/**
 * Determine which form steps should be hidden based on pre-filled data
 * @param prefilledData - Form data that was pre-filled
 * @returns Array of step numbers to hide (1-based indexing)
 */
export function getStepsToHide(prefilledData: PreFilledFormData): number[] {
  const stepsToHide: number[] = []

  // Step 1: Loan Purpose - hide if loan_amount is present
  if (prefilledData.loan_amount) {
    stepsToHide.push(1)
  }

  // Step 2: Your Name - hide if first_name AND last_name are present
  if (prefilledData.first_name && prefilledData.last_name) {
    stepsToHide.push(2)
  }

  // Step 3: Email - hide if email is present
  if (prefilledData.email) {
    stepsToHide.push(3)
  }

  // Step 4: Phone - NEVER HIDE (contains required TCPA/SMS auth checkboxes)
  // Even if phone is pre-filled, user MUST check authorization boxes

  // Step 5: Address - hide if all address fields are present
  if (prefilledData.street && prefilledData.city && prefilledData.state && prefilledData.zip) {
    stepsToHide.push(5)
  }

  // Step 6: Date of Birth - hide if dob is present
  if (prefilledData.dob) {
    stepsToHide.push(6)
  }

  // Step 7-10: Housing, Employment, Income - ALWAYS SHOW (user must fill these)
  // Even if data exists from API, we want user to confirm/update

  // Step 11: SSN - NEVER HIDE (contains required soft_pull authorization checkbox)
  // Even if SSN is pre-filled, user MUST check soft pull authorization

  // Step 12+: Co-applicant - ALWAYS SHOW (unchanged behavior)

  console.log(`📋 Steps to hide: [${stepsToHide.join(', ')}]`)
  console.log(`📋 Steps kept visible for authorizations: [4 (TCPA/SMS), 11 (Soft Pull)]`)

  return stepsToHide
}

/**
 * Get list of field names that need to be collected from user
 * @returns Array of field names
 */
export function getFieldsToCollect(): string[] {
  return [
    'housing_status',
    'housing_payment',
    'employment_status',
    'income',
    'credit_auth',
    'tcpa_auth',
    'sms_auth'
  ]
}

/**
 * Validate that pre-filled data contains minimum required fields
 * @param data - Pre-filled form data
 * @returns true if data is valid for form pre-fill
 */
export function validatePreFilledData(data: PreFilledFormData): boolean {
  // Minimum required: name, email, phone
  const hasRequiredFields = !!(
    data.first_name &&
    data.last_name &&
    data.email &&
    data.phone
  )

  if (!hasRequiredFields) {
    console.warn('⚠️ Pre-filled data missing required fields')
    return false
  }

  // Validate email format (basic check)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (data.email && !emailRegex.test(data.email)) {
    console.warn('⚠️ Invalid email format in pre-filled data')
    return false
  }

  // Validate phone has at least 10 digits
  if (data.phone && data.phone.replace(/\D/g, '').length < 10) {
    console.warn('⚠️ Invalid phone number in pre-filled data')
    return false
  }

  return true
}
